// MCIP-21 — pure view-model logic for the pod-overview screen. No DOM, no
// fetch: everything derives from the /api/pods payload and is unit-tested.
import { CLASSIFICATION_ORDER } from './palette.js';

/**
 * Counted chips in the FIXED classification order (colour follows the entity;
 * the card must not reorder chips as counts shift week to week). Unknown keys
 * the API may grow later append after the canonical order — shown, never dropped.
 */
export function chipCounts(byClassification = {}) {
  const known = CLASSIFICATION_ORDER
    .filter((k) => byClassification[k] > 0)
    .map((k) => ({ classification: k, count: byClassification[k] }));
  const extra = Object.keys(byClassification)
    .filter((k) => !CLASSIFICATION_ORDER.includes(k) && byClassification[k] > 0)
    .sort()
    .map((k) => ({ classification: k, count: byClassification[k] }));
  return [...known, ...extra];
}

/**
 * SVG polyline points for the sparkline, "x,y x,y …" over a viewBox of
 * width × height. Null pass rates (runs that executed nothing) are skipped —
 * a gap in the data must not paint as a 0% crash. Returns null when fewer
 * than two real points exist: a single dot is not a trend.
 */
export function sparkPath(spark, { width = 200, height = 30, pad = 3 } = {}) {
  const pts = (spark || []).filter((p) => p.pass_rate !== null && p.pass_rate !== undefined);
  if (pts.length < 2) return null;
  const rates = pts.map((p) => p.pass_rate);
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  // A flat line (all equal) still needs a y — centre it.
  const spanY = max - min || 1;
  const stepX = (width - pad * 2) / (pts.length - 1);
  return pts
    .map((p, i) => {
      const x = pad + i * stepX;
      const y = max === min
        ? height / 2
        : pad + (1 - (p.pass_rate - min) / spanY) * (height - pad * 2);
      return `${Math.round(x * 100) / 100},${Math.round(y * 100) / 100}`;
    })
    .join(' ');
}

/**
 * The card's single truth-state, driving its accent and empty-state words:
 *   incident — active backend incidents (red accent, list them first)
 *   signals  — open signals, no incidents
 *   quiet    — ran tests, zero open signals (the good kind of empty)
 *   no-data  — no runs in the window (not-harvested ≠ all-green: say which)
 */
export function podState(pod) {
  if (pod.incident_clusters > 0) return 'incident';
  if (pod.signals.total > 0) return 'signals';
  if (pod.latest.pass_rate !== null) return 'quiet';
  return 'no-data';
}
